import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { Form, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import BackButton from "components/Shared/BackButton"
import { addQuestionSchema } from "components/Common/validation"
import {
  addQuestion,
  getQuestionDetails,
  updateQuestion,
} from "store/actions/ratingsAction"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  en: "",
  ar: "",
  type: null,
  scale: null,
  displayOrder: null,
}

const AddQuestion = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"Rating Question "}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, questionDetails } = useSelector(
    state => state.Ratings
  )

  const [formValues, setFormValues] = useState(initialValues)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      27,
      "rating_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      27,
      "rating_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      fetchOldValues()
    }
  }, [])

  const fetchOldValues = () => {
    dispatch(getQuestionDetails(id))
  }

  useEffect(() => {
    if (questionDetails && !loading && mode === "edit") {
      const { question, type, scale, status, displayOrder } = questionDetails
      setFormValues({
        ...formValues,
        en: question?.en,
        ar: question?.ar,
        type,
        scale,
        displayOrder,
      })
    }
  }, [questionDetails])

  const handleOnSubmit = values => {
    const { en, ar, type, scale, displayOrder } = values
    const updatedScale = type == 0 ? 5 : 10
    const payload = {
      question: {
        en,
        ar,
      },
      type,
      scale: updatedScale,
      displayOrder,
    }
    if (mode === "add") {
      dispatch(addQuestion(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(`/rating-questions`)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id,
      }
      dispatch(updateQuestion(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Rating Question")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addQuestionSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="en">
                              <span className="text-danger">*</span>{" "}
                              {t("Question(English)")}
                            </label>
                            <Field
                              type="text"
                              id="en"
                              name="en"
                              placeholder={t("Question(English)")}
                              className={`form-control ${
                                touched.en && errors.en ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="en" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="ar">
                              <span className="text-danger">*</span>{" "}
                              {t("Question(عربي)")}
                            </label>
                            <Field
                              type="text"
                              id="ar"
                              name="ar"
                              placeholder={t("Question(عربي)")}
                              className={`form-control ${
                                touched.ar && errors.ar ? "is-invalid" : ""
                              }`}
                              style={{ direction: "rtl" }}
                            />
                            <TextError name="ar" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="type">
                              <span className="text-danger">*</span>{" "}
                              {t("Question Type")}
                            </label>
                            <Field
                              as="select"
                              id="type"
                              name="type"
                              className={`form-control ${
                                touched.type && errors.type ? "is-invalid" : ""
                              }`}
                              onChange={e => {
                                const selectedValue = e.target.value
                                setFieldValue("type", selectedValue)
                              }}
                            >
                              <option hidden>{t("Select Type")}</option>
                              <option value={0}>{t("Star")}</option>
                              <option value={1}>{t("Scale")}</option>
                            </Field>
                            <TextError name="type" />
                          </div>
                        </div>
                        {/* <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="scale">
                              <span className="text-danger">*</span>{" "}
                              {values.type == 0 ? "Star" : "Scale"}
                            </label>
                            <Field
                              type="text"
                              id="scale"
                              name="scale"
                              placeholder={values.type == 0 ? "Star" : "Scale"}
                              className={`form-control ${
                                touched.scale && errors.scale
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError
                              name="scale"
                              
                            />
                          </div>
                        </div> */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="displayOrder">
                              <span className="text-danger">*</span>{" "}
                              {t("Display Order")}
                            </label>
                            <Field
                              type="text"
                              id="displayOrder"
                              name="displayOrder"
                              placeholder={t("Display Order")}
                              className={`form-control ${
                                touched.displayOrder && errors.displayOrder
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="displayOrder" />
                          </div>
                        </div>
                        <div className="col-12 d-flex justify-content-end mt-3 mb-1">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>
                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddQuestion
